import { RegistrationBase } from '@common/model/registration.base';
import { isNullOrUndefined } from '@common/util';
import { LanguagePreferences } from '@common/model/language-preferences';

export class Contact {
    addressEntryType?: AddressEntryType;
    firstName: string;
    lastName: string;
    city: string;
    zip: string;
    esiId: string;
    address: string;
    addressOverflow: string;
    state: string;
    powerRegion: string;
    utility: string;
    mobilePhone: string;
    phone: string;

    agreesToConditions?: boolean;
    wantsEnvironmentTexasNewsletter?: boolean;

    constructor() {}
}

export enum ExcludedReason {
    PostcodeOutOfRange = 'PostcodeOutOfRange',
    ContractEndDateOutOfSwitchWindow = 'ContractEndDateOutOfSwitchWindow',
}

export namespace Contact {
    export function oneLineAddress(contact: Contact) {
        return (
            (contact.address ? contact.address : '') +
            (contact.addressOverflow ? contact.addressOverflow : '') +
            (contact.addressOverflow || contact.address ? ', ' : '') +
            (contact.city ? contact.city + ' ' : '') +
            (contact.state ? contact.state + ' ' : '') +
            (contact.zip ? contact.zip : '')
        );
    }
    export function oneLineAddressFromAddress(address: Address) {
        return (
            (address.addressLine1 ? address.addressLine1 : '') +
            (address.addressLine2 ? address.addressLine2 : '') +
            (address.addressLine2 || address.addressLine1 ? ', ' : '') +
            (address.city ? address.city + ' ' : '') +
            (address.state ? address.state + ' ' : '') +
            (address.zip ? address.zip : '')
        );
    }
}

export class Product {
    heatingSource?: HeatingSource;
    knowsCurrentEnergyUsage?: boolean;
    electricityCurrentVendor: string;
    elecKwhForMonth?: number;
    kwhForYear?: number;
    hasExpirationDate?: YesNoDontKnow;
    elecCurrentMonthUsage?: UsageMonth;
    expireDate?: Date;
    peopleInHousehold?: PeopleInHousehold;
    houseSize?: HouseSize;
    hasChangedSupplierInLast3Years?: boolean;
    informWhenSwitchPossibleForFree?: boolean;
    hasSolarPanels?: boolean;
    hasBatteryStorage?: boolean;
    agreedForDefaultValues?: boolean;
}

export enum PlanType {
    Fixed = 'Fixed',
    Variable = 'Variable',
    Indexed = 'Indexed',
    DontKnow = 'DontKnow',
}
export namespace PlanType {
    export const values = [
        PlanType.Fixed,
        PlanType.Variable,
        PlanType.Indexed,
        PlanType.DontKnow,
    ];
    export function description(type: PlanType) {
        switch (type) {
            case PlanType.Fixed:
                return 'plan-type-fixed-label';
            case PlanType.Variable:
                return 'plan-type-variable-label';
            case PlanType.Indexed:
                return 'plan-type-indexed-label';
            case PlanType.DontKnow:
                return 'plan-type-dontknow-label';
            default:
                return 'unknown';
        }
    }
}

export enum YesNoDontKnow {
    Yes = 'Yes',
    No = 'No',
    DontKnow = 'DontKnow',
}

export enum HeatingSource {
    Electricity = 'Electricity',
    Gas = 'Gas',
}

export enum OfferType {
    oneYear = 'oneYear',
    twoYear = 'twoYear',
    threeYear = 'threeYear',
}

export class OfferParameters {
    supplierCode: string;
    planCode: string;
    auctionCode: string;
    type: OfferType;
    supplierName: string;
    contractName: string;
    baseChargePerMonth: number;
    energyChargeLowForOncor?: number;
    energyChargeHighForOncor?: number;
    energyChargeLowForTnmp?: number;
    energyChargeHighForTnmp?: number;
    energyChargeLowForCenterpoint?: number;
    energyChargeHighForCenterpoint?: number;
    energyChargeLowForAEPCentral?: number;
    energyChargeHighForAEPCentral?: number;
    energyChargeLowLimitKwh: number;
    minimumUsageFeeLimitKwh: number;
    minimumUsageFee: number;
}

export class TduRates {
    baseChargePerMonth: number;
    deliveryChargePerKwh: number;
}

export class Proposal {
    oneYearProposal: ProposalDetails;
    twoYearsProposal: ProposalDetails;
    threeYearsProposal: ProposalDetails;
    tduRates: TduRates;
}

export class Decision {
    reasonToDeclineDescription: string;
    reasonToDecline?: ReasonToDecline;
    desiredSwitchDate?: Date;
    dateOfBirth?: Date;
    ssn: string;
    communicationLanguagePreference?: LanguagePreferences;
    isMoving?: boolean;
    isMovingToDifferentAddress?: boolean;
    preferredBillingMethod?: PreferredBillingMethod;
    differentMailingAddress?: boolean;
    moveInDate?: Date;
    serviceAddress: Address;
    moveInAddress: Address;
    mailingAddress: Address;
    requiredAuthorization1?: boolean;
    requiredAuthorization2?: boolean;
    requiredAuthorization3?: boolean;
    requiredAuthorization4?: boolean;
    agreesToEarlyTerminationFeeAfterSwitch?: boolean;
    agreesToContactViaSms?: boolean;
    selectedOfferType: OfferType;
    agreesToAutoPay?: boolean;
    hasCriticalCareEquipment?: boolean;
}

export class Registration extends RegistrationBase {
    copiedFromRegistrationId: number;
    languagePreferences?: LanguagePreferences;
    contact: Contact;
    product: Product;
    proposal: Proposal;
    decision: Decision;

    isCommercial: boolean;
    excluded: boolean;
    excludedReason?: ExcludedReason;
    exportedUtc?: Date;

    constructor() {
        super();
        this.contact = new Contact();
        this.product = new Product();
        this.proposal = new Proposal();
        this.decision = new Decision();
    }
    get isInInterestedIndividualFlow(): boolean {
        return !this.auction;
    }
}

export namespace Registration {
    export function minimalCompleted(registration: Registration): boolean {
        return !isNullOrUndefined(registration.email);
    }
    export function contactCompleted(registration: Registration): boolean {
        return (
            registration.contact !== null &&
            registration.contact !== undefined &&
            registration.contact.esiId !== null &&
            registration.contact.esiId !== undefined &&
            ((registration.contact.mobilePhone !== null &&
                registration.contact.mobilePhone !== undefined) ||
                (registration.contact.phone !== null &&
                    registration.contact.phone !== undefined)) &&
            registration.contact.agreesToConditions !== null &&
            registration.contact.agreesToConditions !== undefined
        );
    }
    export function productStarted(registration: Registration): boolean {
        return (
            registration &&
            registration.product !== null &&
            registration.product.heatingSource !== null &&
            registration.product.heatingSource !== undefined
        );
    }
    export function isPersonalDataReady(registration: Registration): boolean {
        return false; // !isNullOrUndefined(registration.email);
    }

    export function isProductDataReady(registration: Registration): boolean {
        return false;
    }
}

export enum AddressEntryType {
    esiId = 'EsiId',
    address = 'Address',
    manual = 'Manual',
}

export enum HouseSize {
    Small = 'Small',
    Medium = 'Medium',
    Large = 'Large',
    ExtraLarge = 'ExtraLarge',
}

export namespace HouseSize {
    export const values = [
        HouseSize.Small,
        HouseSize.Medium,
        HouseSize.Large,
        HouseSize.ExtraLarge,
    ];
    export function description(type: HouseSize) {
        switch (type) {
            case HouseSize.Small:
                return 'house-size-small-label';
            case HouseSize.Medium:
                return 'house-size-medium-label';
            case HouseSize.Large:
                return 'house-size-large-label';
            case HouseSize.ExtraLarge:
                return 'house-size-extralarge-label';
            default:
                return 'unknown';
        }
    }
    export function getNumber(type: HouseSize) {
        switch (type) {
            case HouseSize.Small:
                return 1;
            case HouseSize.Medium:
                return 2;
            case HouseSize.Large:
                return 3;
            case HouseSize.ExtraLarge:
                return 4;
        }
    }
}

export enum PeopleInHousehold {
    One = 'One',
    Two = 'Two',
    Three = 'Three',
    Four = 'Four',
    Five = 'Five',
    MoreThanFive = 'MoreThanFive',
}

export namespace PeopleInHousehold {
    export const values = [
        PeopleInHousehold.One,
        PeopleInHousehold.Two,
        PeopleInHousehold.Three,
        PeopleInHousehold.Four,
        PeopleInHousehold.Five,
        PeopleInHousehold.MoreThanFive,
    ];
    export function getNumber(type: PeopleInHousehold) {
        switch (type) {
            case PeopleInHousehold.One:
                return 1;
            case PeopleInHousehold.Two:
                return 2;
            case PeopleInHousehold.Three:
                return 3;
            case PeopleInHousehold.Four:
                return 4;
            case PeopleInHousehold.Five:
                return 5;
            case PeopleInHousehold.MoreThanFive:
                return 6;
        }
    }
    export function description(type: PeopleInHousehold) {
        switch (type) {
            case PeopleInHousehold.One:
                return '1';
            case PeopleInHousehold.Two:
                return '2';
            case PeopleInHousehold.Three:
                return '3';
            case PeopleInHousehold.Four:
                return '4';
            case PeopleInHousehold.Five:
                return '5';
            case PeopleInHousehold.MoreThanFive:
                return '>5';
        }
    }
}
export enum UsageMonth {
    January = 'January',
    February = 'February',
    March = 'March',
    April = 'April',
    May = 'May',
    June = 'June',
    July = 'July',
    August = 'August',
    September = 'September',
    October = 'October',
    November = 'November',
    December = 'December',
}

export class EstimatedCosts {
    estimatedOfferParameters: OfferParameters;
    marketAverageSuppliersTotalCosts: SupplierTotalCost[];
    tduAverageCharges: TduRates;
}

export class ProposalDetails {
    winningSupplierTotalCost: number;
    estimatedMarketSupplierTotalCost: number;
    savings: number;
    pucFeePercentage: number;
    offerParameters: OfferParameters;
    averagePricesPerKwh: AveragePricesPerKwh;
}

export class AveragePricesPerKwh {
    for500Kwh: number;
    for1000Kwh: number;
    for2000Kwh: number;
}

export class SupplierTotalCost {
    supplierCode: string;
    planCode: string;
    totalCost: number;
}

export class Address {
    addressLine1: string;
    addressLine2: string;
    city: string;
    zip: string;
    state: string;
}

export enum ReasonToDecline {
    NoReason = 'NoReason',
    DontLikeTheVendor = 'DontLikeTheVendor',
    TerminationFee = 'TerminationFee',
    HadBetterOffer = 'HadBetterOffer',
    DontLikeThePriceOfTheOffer = 'DontLikeThePriceOfTheOffer',
    FoundBetterRateMyself = 'FoundBetterRateMyself',
    OfferWasUnclear = 'OfferWasUnclear',
    AlreadyCustomer = 'AlreadyCustomer',
    SwitchedViaOtherWay = 'SwitchedViaOtherWay',
    Other = 'Other',
}

export enum PreferredBillingMethod {
    Email = 'Email',
    Mail = 'Mail',
    Both = 'Both',
}

export namespace PreferredBillingMethod {
    export function description(method: PreferredBillingMethod) {
        if (method === PreferredBillingMethod.Both) {
            return `${PreferredBillingMethod.Email} & ${PreferredBillingMethod.Mail}`;
        }

        return PreferredBillingMethod[method];
    }
}
